import React, { useEffect } from "react";
import { User, RefreshCw } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux";
import { getUserProfileAction } from "../../../store/slices/userProfile/userProfileSliceActions";
import { API_STATUS } from "../../../constants/apiStatus";
import { colors } from "../../../constants/colors";

const UserProfileSection = ({ uid }) => {
  const dispatch = useAppDispatch();
  const { profile, apiStatus, error } = useAppSelector(
    (state) => state.userProfile
  );

  const isLoadingProfile = apiStatus.getProfile === API_STATUS.STARTED;

  // Load user profile when component mounts or uid changes
  useEffect(() => {
    if (uid) {
      dispatch(getUserProfileAction(uid));
    }
  }, [uid, dispatch]);

  // Format profile data for display
  const formatProfileValue = (key, value) => {
    if (value === null || value === undefined) return "Not specified";
    if (typeof value === "boolean") return value ? "Yes" : "No";

    // Handle arrays (like sicknesses)
    if (Array.isArray(value)) {
      if (value.length === 0) return "None";
      return (
        <ul style={{ margin: 0, paddingLeft: "1rem" }}>
          {value.map((item, index) => (
            <li key={index} style={{ marginBottom: "0.25rem" }}>
              {typeof item === "object" ? JSON.stringify(item) : item}
            </li>
          ))}
        </ul>
      );
    }

    // Handle specific objects
    if (typeof value === "object") {
      // Handle empty objects or objects that should show "Not set"
      if (Object.keys(value).length === 0) {
        return "Not set";
      }

      // Handle timestamp objects with _seconds and _nanoseconds
      if (value._seconds && value._nanoseconds !== undefined) {
        const date = new Date(value._seconds * 1000);
        return (
          <div
            style={{
              backgroundColor: "#f3f4f6",
              padding: "0.5rem",
              borderRadius: "6px",
              fontSize: "0.875rem",
            }}
          >
            <div style={{ fontWeight: "600", color: "#374151" }}>
              {date.toLocaleString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </div>
            <div
              style={{
                fontSize: "0.75rem",
                color: "#6b7280",
                marginTop: "0.25rem",
              }}
            >
              Timestamp: {value._seconds}.{value._nanoseconds}
            </div>
          </div>
        );
      }

      // Handle aiNutritionTarget
      if (key === "aiNutritionTarget") {
        return (
          <div
            style={{
              backgroundColor: "#ecfdf5",
              border: "1px solid #d1fae5",
              borderRadius: "8px",
              padding: "1rem",
              fontSize: "0.875rem",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "0.75rem",
              }}
            >
              {Object.entries(value).map(([nutrientKey, nutrientValue]) => {
                // Determine unit based on nutrient type
                let unit = "";
                if (
                  nutrientKey.includes("calories") ||
                  nutrientKey.includes("Calories")
                ) {
                  unit = " kcal";
                } else if (
                  nutrientKey.includes("weight") ||
                  nutrientKey.includes("Weight")
                ) {
                  unit = " kg";
                } else if (
                  nutrientKey.includes("height") ||
                  nutrientKey.includes("Height")
                ) {
                  unit = " cm";
                } else if (nutrientKey !== "idealWeight") {
                  unit = " g";
                }

                return (
                  <div key={nutrientKey}>
                    <div
                      style={{
                        color: "#065f46",
                        fontSize: "0.75rem",
                        fontWeight: "600",
                      }}
                    >
                      {nutrientKey.replace(/([A-Z])/g, " $1").toUpperCase()}
                    </div>
                    <div style={{ fontWeight: "600", color: "#059669" }}>
                      {nutrientValue}
                      {unit}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      // Handle detailed nutrition calculation data
      if (
        key === "detailedNutrition" ||
        (value.hasOwnProperty("phase") &&
          value.hasOwnProperty("method") &&
          value.hasOwnProperty("bmr"))
      ) {
        return (
          <div
            style={{
              backgroundColor: "#fef3c7",
              border: "1px solid #fcd34d",
              borderRadius: "8px",
              padding: "1rem",
              fontSize: "0.875rem",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "0.75rem",
              }}
            >
              {value.phase && (
                <div>
                  <div
                    style={{
                      color: "#92400e",
                      fontSize: "0.75rem",
                      fontWeight: "600",
                    }}
                  >
                    PHASE
                  </div>
                  <div
                    style={{
                      fontWeight: "600",
                      color: "#d97706",
                      textTransform: "capitalize",
                    }}
                  >
                    {value.phase}
                  </div>
                </div>
              )}
              {value.method && (
                <div>
                  <div
                    style={{
                      color: "#92400e",
                      fontSize: "0.75rem",
                      fontWeight: "600",
                    }}
                  >
                    METHOD
                  </div>
                  <div style={{ fontWeight: "600", color: "#d97706" }}>
                    {value.method
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </div>
                </div>
              )}
              {value.bmr && (
                <div>
                  <div
                    style={{
                      color: "#92400e",
                      fontSize: "0.75rem",
                      fontWeight: "600",
                    }}
                  >
                    BMR
                  </div>
                  <div style={{ fontWeight: "600", color: "#3b82f6" }}>
                    {value.bmr} kcal
                  </div>
                </div>
              )}
              {value.tdee && (
                <div>
                  <div
                    style={{
                      color: "#92400e",
                      fontSize: "0.75rem",
                      fontWeight: "600",
                    }}
                  >
                    TDEE
                  </div>
                  <div style={{ fontWeight: "600", color: "#10b981" }}>
                    {value.tdee} kcal
                  </div>
                </div>
              )}
              {value.adjustmentFactor && (
                <div>
                  <div
                    style={{
                      color: "#92400e",
                      fontSize: "0.75rem",
                      fontWeight: "600",
                    }}
                  >
                    ADJUSTMENT FACTOR
                  </div>
                  <div style={{ fontWeight: "600", color: "#d97706" }}>
                    {value.adjustmentFactor}
                  </div>
                </div>
              )}
              {value.targetCalories && (
                <div>
                  <div
                    style={{
                      color: "#92400e",
                      fontSize: "0.75rem",
                      fontWeight: "600",
                    }}
                  >
                    TARGET CALORIES
                  </div>
                  <div style={{ fontWeight: "600", color: "#f59e0b" }}>
                    {value.targetCalories} kcal
                  </div>
                </div>
              )}
            </div>
            {value.calculatedAt && (
              <div
                style={{
                  marginTop: "0.75rem",
                  paddingTop: "0.75rem",
                  borderTop: "1px solid #fbbf24",
                  fontSize: "0.75rem",
                  color: "#92400e",
                }}
              >
                <strong>Calculated:</strong>{" "}
                {new Date(value.calculatedAt).toLocaleString()}
              </div>
            )}
          </div>
        );
      }

      // For any other object that we haven't specifically handled, create a clean display
      return (
        <div
          style={{
            backgroundColor: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "1rem",
            fontSize: "0.875rem",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "0.75rem",
            }}
          >
            {Object.entries(value).map(([subKey, subValue]) => (
              <div key={subKey}>
                <div
                  style={{
                    color: "#6b7280",
                    fontSize: "0.75rem",
                    fontWeight: "600",
                  }}
                >
                  {subKey.replace(/([A-Z])/g, " $1").toUpperCase()}
                </div>
                <div style={{ fontWeight: "600", color: "#374151" }}>
                  {typeof subValue === "object" && subValue !== null
                    ? JSON.stringify(subValue)
                    : subValue === null || subValue === undefined
                    ? "Not set"
                    : String(subValue)}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return value.toString();
  };

  // Styles
  const cardStyle = {
    backgroundColor: colors.white,
    borderRadius: "12px",
    padding: "1.5rem",
    marginBottom: "2rem",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  };

  const sectionTitleStyle = {
    fontSize: "1.25rem",
    fontWeight: "600",
    color: colors.darkBlue,
    marginBottom: "1rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
  };

  const thStyle = {
    backgroundColor: colors.extraLightGray,
    color: colors.darkGray,
    padding: "0.75rem",
    textAlign: "left",
    fontWeight: "600",
    fontSize: "0.875rem",
    borderBottom: `2px solid ${colors.doubleExtraLightGray}`,
  };

  const tdStyle = {
    padding: "0.75rem",
    borderBottom: `1px solid ${colors.doubleExtraLightGray}`,
    fontSize: "0.875rem",
  };

  if (error) {
    return (
      <div style={cardStyle} className="admin-card">
        <div style={sectionTitleStyle}>
          <User size={20} />
          User Profile
        </div>
        <div
          style={{
            backgroundColor: "#fee2e2",
            color: "#dc2626",
            padding: "1rem",
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle} className="admin-card">
      <div style={sectionTitleStyle}>
        <User size={20} />
        User Profile
        {isLoadingProfile && <RefreshCw size={16} className="animate-spin" />}
      </div>

      {isLoadingProfile ? (
        <div
          style={{
            padding: "3rem",
            textAlign: "center",
            color: colors.darkGray,
          }}
        >
          Loading user profile...
        </div>
      ) : profile ? (
        <div className="admin-table-container">
          <table style={tableStyle}>
            <tbody>
              {Object.entries(profile).map(([key, value]) => (
                <tr key={key}>
                  <td style={{ ...thStyle, width: "30%" }}>
                    {key.charAt(0).toUpperCase() +
                      key.slice(1).replace(/([A-Z])/g, " $1")}
                  </td>
                  <td style={tdStyle}>{formatProfileValue(key, value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          style={{
            padding: "3rem",
            textAlign: "center",
            color: colors.darkGray,
          }}
        >
          No profile data found for this user.
        </div>
      )}
    </div>
  );
};

export default UserProfileSection;
